import type { Router } from "vue-router";
import { useScribeStore } from "@/stores/scribe";
import { useEventsStore } from "@/stores/eventsStore";
import { auth } from "@/auth";
import { sparkyColdStartDiagnostics } from "@/services/sparkyColdStartDiagnostics";

// Function to check if current route should start monitoring
const shouldStartMonitoring = (path: string): { should: boolean; type: string | null; id: string | null } => {
	// Check for project routes: /projects/:id
	const projectMatch = path.match(/^\/projects\/([^\/\?]+)/);
	if (projectMatch) {
		return { should: true, type: "project", id: projectMatch[1] };
	}

	// Check for conversation routes: /conversations/:id
	const conversationMatch = path.match(/^\/conversations\/([^\/\?]+)/);
	if (conversationMatch) {
		return { should: true, type: "conversation", id: conversationMatch[1] };
	}

	return { should: false, type: null, id: null };
};

// Vue plugin
const ScribeMonitor = {
	install(_app: any, router: Router) {
		// Set up SSE connection and wire handlers to scribe store
		const eventsStore = useEventsStore();
		const scribeStore = useScribeStore();

		eventsStore.addHandlers({
			onAnalysisCreated: (event) => scribeStore.handleAnalysisEvent(event),
			onAnalysisUpdated: (event) => scribeStore.handleAnalysisEvent(event),
			onAnalysisDeleted: (event) => scribeStore.handleAnalysisEvent(event),
			onConnected: () => {
				// Recover from any missed events during disconnect
				scribeStore.refreshAnalyses(true);
			}
		});

		// Start SSE connection
		eventsStore.connect();

		// LSP-19970: last-issued route wins across the async auth boundary. This is plugin-install scoped so an
		// abandoned route can never start or stop monitoring after a newer navigation takes ownership.
		let routeChangeSequence = 0;

		// Function to handle route changes
		const handleRouteChange = async (to: { path: string }) => {
			const routeSequence = ++routeChangeSequence;
			const { should, type, id } = shouldStartMonitoring(to.path);
			console.debug("ScribeMonitor: route change received", {
				routeSequence,
				shouldStartMonitoring: should,
				routeType: type
			});

			if (should && type && id) {
				// Check authentication before starting monitoring
				const isAuthenticated = await auth.isAuthenticated();
				if (routeSequence !== routeChangeSequence) {
					console.debug("ScribeMonitor: discarding superseded route auth result", {
						routeSequence,
						currentRouteSequence: routeChangeSequence,
						routeType: type
					});
					return;
				}
				if (isAuthenticated) {
					await scribeStore.startMonitoring(id, type);
				} else {
					if (sparkyColdStartDiagnostics.claim("scribe_auth_skipped")) {
						sparkyColdStartDiagnostics
							.startPhase("scribe_auth_skipped", { routeType: type === "project" || type === "conversation" ? type : "other" })
							.finish("skipped");
					}
					console.debug("ScribeMonitor: User not authenticated, skipping monitoring for", type, id);
					scribeStore.stopMonitoring();
				}
			} else {
				scribeStore.stopMonitoring();
			}
		};

		// Monitor route changes (afterEach so navigation is not blocked by async work)
		router.afterEach((to) => {
			void handleRouteChange(to);
		});

		// Check current route on plugin installation (for page refreshes)
		if (router.currentRoute.value) {
			setTimeout(() => {
				void handleRouteChange(router.currentRoute.value);
			}, 0);
		}
	}
};

export default ScribeMonitor;
